import React, { useState, useEffect, useRef } from 'react';
import {
  Building2, FileText, CheckCircle2, Clock, Search,
  RefreshCw, Copy, Check, ExternalLink, AlertCircle,
  Eye, Lock, Globe, Briefcase, Scale, Calculator, UserCheck,
  FileCheck, Sparkles, Bot, Phone, Mail, MapPin, CheckSquare,
  LogOut, Plus, ChevronRight, X, Filter, Trash2, Edit3,
  Download, ArrowRight, ShieldCheck, MessageSquare, Volume2,
  Calendar, Layers, UserPlus, AlertTriangle, Mic, MicOff,
  UploadCloud, Send, Shield, Save, Printer, HelpCircle,
  TrendingUp, DollarSign, PieChart, FileCode, CheckCircle,
  Maximize2, Minimize2
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { LunaVoiceModel } from '../LunaVoiceModel';
import { LunaFace } from '../LunaFace';

interface CorporateClientPortalProps {
  token: string;
  onLogout: () => void;
}

const CORPORATE_SERVICES = [
  { id: 'tax_filling', label: 'Business Tax & Return Filing', icon: Calculator, desc: 'Individual & Corporate Income Tax (Y/A 2024/2025), VAT, SVAT & Withholding Tax schedules', color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30' },
  { id: 'secretarial', label: 'Company Secretarial Services', icon: Briefcase, desc: 'Annual Returns (Form 13), Board Resolutions, Director/Shareholder changes (Form 20), Company Incorporation (Form 1)', color: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/30' },
  { id: 'hrm', label: 'HRM & Payroll Compliance', icon: Users, desc: 'EPF/ETF Form C submissions, Employee Onboarding dockets, Salary structuring & Employment contracts', color: 'text-pink-400', bg: 'bg-pink-500/10', border: 'border-pink-500/30' },
  { id: 'accounting', label: 'Accounting & Bookkeeping', icon: FileText, desc: 'Monthly Trial Balance, Profit & Loss ledgers, Balance Sheet schedules & Fixed Asset registers', color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/30' },
  { id: 'auditing_internal', label: 'Internal Auditing', icon: ShieldCheck, desc: 'Internal control verifications, Stock count audits, Petty cash audits & Risk assessment dockets', color: 'text-indigo-400', bg: 'bg-indigo-500/10', border: 'border-indigo-500/30' },
  { id: 'auditing_external', label: 'External Auditing Prep', icon: FileCheck, desc: 'Statutory external audit file preparation, Bank confirmation dockets & Balance reconciliations', color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/30' },
  { id: 'legal', label: 'Legal & Contract Services', icon: Scale, desc: 'Non-Disclosure Agreements (NDA), Service Level Agreements (SLA), Commercial Leases & Legal opinions', color: 'text-rose-400', bg: 'bg-rose-500/10', border: 'border-rose-500/30' },
  { id: 'consulting', label: 'Business Consulting & Strategy', icon: Globe, desc: 'Feasibility studies, Business expansion plans, Restructuring proposals & Bank loan viability reports', color: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/30' },
  { id: 'other', label: 'Other Corporate Advisory', icon: Sparkles, desc: 'Specialized regulatory licensing, Trademark registration & Custom advisory submissions', color: 'text-teal-400', bg: 'bg-teal-500/10', border: 'border-teal-500/30' },
];

function Users(props: any) {
  return <Building2 {...props} />;
}

export default function CorporateClientPortal({ token, onLogout }: CorporateClientPortalProps) {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'my_filings' | 'form_filing'>('dashboard');
  const [user, setUser] = useState<any>(null);
  const [firm, setFirm] = useState<any>(null);
  const [myFilings, setMyFilings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Active Filing Form State
  const [activeServiceId, setActiveServiceId] = useState<string>('tax_filling');
  const [activeFilingId, setActiveFilingId] = useState<number | null>(null);
  const [activeSchedule, setActiveSchedule] = useState<string>('header');
  const [formData, setFormData] = useState<any>({
    tinNumber: '',
    fullName: '',
    assessmentYear: '2024/2025',
    employmentIncome: [],
    businessIncomeDeclared: 0,
    grossBusinessRevenue: 0,
    salariesExpenses: 0,
    rentUtilitiesExpenses: 0,
    transportExpenses: 0,
    stationeryExpenses: 0,
    otherExpenses: 0,
    totalExpenses: 0,
    netBusinessIncome: 0,
    solarInstalled: 'no',
    lands: [],
    vehicles: [],
    bankBalances: [],
    cashInHand: 0,
    liabilities: [],
    secretarialFormType: 'Form 13 - Annual Return',
    hrmFilingType: 'EPF/ETF Monthly Form C',
    accountingPeriod: 'Monthly',
    auditType: 'Annual Statutory',
    legalAgreementType: 'Non-Disclosure Agreement (NDA)',
    consultingGoal: 'Business Expansion Feasibility',
    generalNotes: ''
  });
  const [uploadedDocs, setUploadedDocs] = useState<any[]>([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [savingFiling, setSavingFiling] = useState(false);

  // ── Tenet Luna Floating / Docked / Fullscreen State ──
  const [isLunaOpen, setIsLunaOpen] = useState(false);
  const [lunaViewMode, setLunaViewMode] = useState<'docked' | 'fullscreen'>('docked');

  const authHeader = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  });

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 4000);
  };

  // Fetch User & Firm Profile
  const fetchUserData = async () => {
    try {
      const res = await fetch('/api/corporate/user/me', { headers: authHeader() });
      const data = await res.json();
      if (data.success) {
        setUser(data.user);
        setFirm(data.firm);
        setFormData((prev: any) => ({
          ...prev,
          fullName: data.user.fullName || prev.fullName,
          tinNumber: data.user.tinNumber || prev.tinNumber
        }));
      }
    } catch (err) {
      console.error('Error fetching user data:', err);
    }
  };

  // Fetch Filings
  const fetchMyFilings = async () => {
    try {
      const res = await fetch('/api/corporate/user/filings', { headers: authHeader() });
      const data = await res.json();
      if (data.success) {
        setMyFilings(data.filings || []);
      }
    } catch (err) {
      console.error('Error loading filings:', err);
    }
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchUserData(), fetchMyFilings()]);
      setLoading(false);
    };
    init();
  }, [token]);

  // 🌟 Listen to Real-Time Tenet Luna Corporate Action Events
  useEffect(() => {
    const handleCorporateAction = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (!detail) return;

      console.log('[Tenet Luna Corporate Action Event Received]', detail);

      if (detail.action === 'SWITCH_TAB' && detail.tabId) {
        const tabMap: Record<string, string> = {
          'general': 'header',
          'header': 'header',
          'schedule1': 'sched1',
          'sched1': 'sched1',
          'schedule2': 'sched2',
          'sched2': 'sched2',
          'schedule3': 'sched3',
          'sched3': 'sched3',
          'schedule4': 'sched4',
          'sched4': 'sched4',
          'schedule7': 'sched7',
          'sched7': 'sched7'
        };
        setActiveSchedule(tabMap[detail.tabId] || detail.tabId);
        showToast(`Tenet Luna opened schedule: ${detail.tabId.toUpperCase()}`);
      } else if (detail.action === 'FILL_FIELD' && detail.fieldKey) {
        setFormData((prev: any) => {
          const next = { ...prev, [detail.fieldKey]: detail.fieldValue };
          const gross = Number(next.grossBusinessRevenue || 0);
          const totalExp = Number(next.salariesExpenses || 0) +
                           Number(next.rentUtilitiesExpenses || 0) +
                           Number(next.transportExpenses || 0) +
                           Number(next.stationeryExpenses || 0) +
                           Number(next.otherExpenses || 0);
          next.totalExpenses = totalExp;
          next.netBusinessIncome = Math.max(0, gross - totalExp);
          return next;
        });
        showToast(`Tenet Luna populated: ${detail.fieldKey}`);
      } else if (detail.action === 'SAVE_DRAFT') {
        handleSaveFiling(false);
      } else if (detail.action === 'SUBMIT_FILING') {
        handleSaveFiling(true);
      }
    };

    window.addEventListener('kangavena:corporate-action', handleCorporateAction);
    return () => window.removeEventListener('kangavena:corporate-action', handleCorporateAction);
  }, [activeServiceId, activeFilingId, formData, uploadedDocs]);

  // Handle Document Upload
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingDoc(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result as string;
        const res = await fetch('/api/corporate/user/upload', {
          method: 'POST',
          headers: authHeader(),
          body: JSON.stringify({
            fileName: file.name,
            mimeType: file.type,
            base64Content: base64
          })
        });
        const data = await res.json();
        if (data.success) {
          setUploadedDocs(prev => [...prev, data.file]);
          showToast(`Uploaded "${file.name}"! Tenet Luna is ready to extract data.`);
        } else {
          showToast(data.error || 'Upload failed');
        }
      };
      reader.readAsDataURL(file);
    } catch (err: any) {
      showToast(err?.message || 'Error uploading file');
    } finally {
      setUploadingDoc(false);
    }
  };

  // Save or Submit Filing
  const handleSaveFiling = async (isFinalSubmit: boolean) => {
    setSavingFiling(true);
    try {
      const serviceObj = CORPORATE_SERVICES.find(s => s.id === activeServiceId);
      const title = `${serviceObj?.label || 'Corporate Filing'} - ${user?.businessName || user?.fullName} (Y/A 2024/2025)`;

      const endpoint = activeFilingId
        ? `/api/corporate/user/filings/${activeFilingId}`
        : '/api/corporate/user/filings';

      const method = activeFilingId ? 'PUT' : 'POST';

      const res = await fetch(endpoint, {
        method,
        headers: authHeader(),
        body: JSON.stringify({
          serviceType: activeServiceId,
          formTitle: title,
          formData,
          extractedDocuments: uploadedDocs,
          status: isFinalSubmit ? 'in_review' : 'draft'
        })
      });

      const data = await res.json();
      if (data.success) {
        if (isFinalSubmit) {
          showToast('🎉 Filing submitted successfully to your firm for final review!');
          setActiveTab('my_filings');
        } else {
          setActiveFilingId(data.filing.id);
          showToast('Draft progress saved successfully!');
        }
        fetchMyFilings();
      } else {
        showToast(data.error || 'Failed to save filing');
      }
    } catch (err: any) {
      showToast(err?.message || 'Error saving filing');
    } finally {
      setSavingFiling(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
      case 'filed':
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 flex items-center gap-1"><CheckCircle2 size={12} /> {status.toUpperCase()}</span>;
      case 'in_review':
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-amber-500/20 text-amber-300 border border-amber-500/30 flex items-center gap-1"><Clock size={12} /> IN REVIEW</span>;
      case 'rejected':
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-rose-500/20 text-rose-300 border border-rose-500/30 flex items-center gap-1"><AlertTriangle size={12} /> REJECTED</span>;
      default:
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-slate-500/20 text-slate-300 border border-slate-500/30 flex items-center gap-1"><Clock size={12} /> DRAFT</span>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex flex-col items-center justify-center gap-4">
        <RefreshCw className="w-8 h-8 text-blue-400 animate-spin" />
        <p className="text-sm font-mono text-slate-400">Loading your Corporate Filing Workspace...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030712] text-slate-100 flex flex-col font-sans selection:bg-blue-500 selection:text-white relative">
      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed top-6 right-6 z-50 bg-blue-600 text-white px-5 py-3 rounded-2xl shadow-2xl border border-blue-400/40 flex items-center gap-3 animate-fade-in text-sm font-bold">
          <Sparkles className="w-4 h-4 text-blue-200" />
          {toastMessage}
        </div>
      )}

      {/* Top Navbar */}
      <header className="sticky top-0 z-40 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800/80 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-tr from-blue-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/20 border border-blue-400/30">
            <Building2 className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-base font-black tracking-tight text-white">{firm?.name || 'Corporate Advisory Portal'}</h1>
              <span className="px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-wider bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">CLIENT PORTAL</span>
            </div>
            <p className="text-xs text-slate-400 font-mono">Client: {user?.fullName} {user?.tinNumber ? `(TIN: ${user.tinNumber})` : ''}</p>
          </div>
        </div>

        {/* Global Quick Tabs */}
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-slate-900/90 p-1.5 rounded-2xl border border-slate-800">
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 ${
                activeTab === 'dashboard' ? 'bg-blue-600 text-white shadow-md shadow-blue-600/30' : 'text-slate-400 hover:text-white'
              }`}
            >
              <Layers size={14} /> Services Hub
            </button>
            <button
              onClick={() => setActiveTab('my_filings')}
              className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 ${
                activeTab === 'my_filings' ? 'bg-blue-600 text-white shadow-md shadow-blue-600/30' : 'text-slate-400 hover:text-white'
              }`}
            >
              <FileText size={14} /> My Submissions ({myFilings.length})
            </button>
          </div>

          {/* Tenet Luna Voice Co-Pilot Launcher */}
          <button
            onClick={() => {
              setIsLunaOpen(true);
              setLunaViewMode('docked');
            }}
            className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-2xl bg-gradient-to-r from-cyan-500 via-blue-600 to-indigo-600 text-white font-black text-xs shadow-lg shadow-cyan-500/25 hover:from-cyan-400 hover:to-blue-500 transition-all border border-cyan-300/30"
          >
            <Bot size={15} /> Talk to Tenet Luna
          </button>
        </div>

        {/* Right Logout */}
        <div className="flex items-center gap-3">
          <button
            onClick={onLogout}
            className="flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-bold bg-rose-500/10 text-rose-400 border border-rose-500/20 hover:bg-rose-500/20 transition-all"
          >
            <LogOut size={13} /> Sign Out
          </button>
        </div>
      </header>

      {/* Main Container */}
      <main className="flex-1 p-6 max-w-7xl mx-auto w-full space-y-6">

        {/* ════════════════════════════════════════════════════════════════════════
            VIEW 1: SERVICES HUB (DASHBOARD)
        ════════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'dashboard' && (
          <div className="space-y-6 animate-fade-in">
            {/* Welcome Banner */}
            <div className="bg-gradient-to-r from-blue-900/40 via-indigo-900/30 to-purple-900/40 border border-blue-500/30 rounded-3xl p-6 sm:p-8 flex flex-col md:flex-row items-center justify-between gap-6 shadow-2xl relative overflow-hidden">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-blue-400" />
                  <span className="text-xs font-bold uppercase tracking-widest text-blue-300">Autonomous Corporate Filing Suite</span>
                </div>
                <h2 className="text-2xl font-black text-white">Welcome, {user?.fullName}!</h2>
                <p className="text-sm text-slate-300 max-w-xl">
                  Select a corporate filing service below. Our embedded **Tenet Luna Voice AI** will guide you step-by-step to complete your statutory schedules hands-free with voice.
                </p>
              </div>

              <div className="bg-slate-950/80 p-4 rounded-2xl border border-slate-800 text-xs space-y-1 font-mono">
                <div className="text-slate-400">Business Entity: <strong className="text-white">{user?.businessName || 'Individual / SME'}</strong></div>
                <div className="text-slate-400">TIN Number: <strong className="text-cyan-400">{user?.tinNumber || 'Pending'}</strong></div>
                <div className="text-slate-400">Advisory Firm: <strong className="text-blue-400">{firm?.name}</strong></div>
              </div>
            </div>

            {/* Service Grid */}
            <div className="space-y-3">
              <h3 className="text-base font-bold text-white flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-blue-400" /> Available Corporate & Legal Services
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {CORPORATE_SERVICES.map((s) => {
                  const Icon = s.icon;
                  return (
                    <div
                      key={s.id}
                      onClick={() => {
                        setActiveServiceId(s.id);
                        setActiveFilingId(null);
                        setActiveTab('form_filing');
                      }}
                      className="bg-slate-900/60 backdrop-blur-md border border-slate-800 rounded-3xl p-6 hover:border-blue-500/50 hover:bg-blue-950/20 transition-all cursor-pointer group flex flex-col justify-between space-y-4"
                    >
                      <div className="space-y-3">
                        <div className={`w-12 h-12 rounded-2xl ${s.bg} ${s.border} border flex items-center justify-center ${s.color}`}>
                          <Icon size={24} />
                        </div>
                        <div>
                          <h4 className="text-base font-bold text-white group-hover:text-blue-300 transition-colors">{s.label}</h4>
                          <p className="text-xs text-slate-400 mt-1 leading-relaxed">{s.desc}</p>
                        </div>
                      </div>

                      <div className="pt-3 border-t border-slate-800/60 flex items-center justify-between text-xs font-bold text-blue-400">
                        <span className="flex items-center gap-1.5">
                          <Bot size={14} /> Tenet Luna Voice Enabled
                        </span>
                        <span className="flex items-center gap-1 group-hover:translate-x-1 transition-transform">
                          Open Filing <ChevronRight size={14} />
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════════
            VIEW 2: MY SUBMISSIONS (STATUS TRACKER)
        ════════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'my_filings' && (
          <div className="space-y-6 animate-fade-in">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-white">My Statutory Filings & Status</h2>
                <p className="text-xs text-slate-400">Track real-time progress and reviewer notes from your firm</p>
              </div>
              <button
                onClick={() => setActiveTab('dashboard')}
                className="px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs flex items-center gap-2"
              >
                <Plus size={14} /> New Filing Submission
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {myFilings.map((filing) => (
                <div
                  key={filing.id}
                  className="bg-slate-900/60 backdrop-blur-md border border-slate-800 rounded-3xl p-5 space-y-4 flex flex-col justify-between"
                >
                  <div className="space-y-3">
                    <div className="flex items-start justify-between">
                      <span className="text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-lg bg-slate-800 text-slate-300 font-mono">
                        {filing.serviceType?.replace('_', ' ')}
                      </span>
                      {getStatusBadge(filing.status)}
                    </div>

                    <div>
                      <h3 className="text-base font-bold text-white line-clamp-1">{filing.formTitle}</h3>
                      <p className="text-xs text-slate-400 font-mono mt-0.5">
                        Updated: {new Date(filing.updatedAt).toLocaleDateString()}
                      </p>
                    </div>

                    {filing.reviewerNotes && (
                      <div className="bg-slate-950/80 p-3 rounded-2xl border border-slate-800 text-xs">
                        <div className="text-[10px] font-bold text-blue-400 uppercase tracking-wider mb-1">Reviewer Feedback:</div>
                        <p className="text-slate-300 italic">{filing.reviewerNotes}</p>
                      </div>
                    )}
                  </div>

                  <div className="pt-3 border-t border-slate-800/60 flex items-center justify-between gap-2">
                    <button
                      onClick={() => {
                        setActiveServiceId(filing.serviceType);
                        setActiveFilingId(filing.id);
                        setFormData(filing.formData || {});
                        setUploadedDocs(filing.extractedDocuments || []);
                        setActiveTab('form_filing');
                      }}
                      className="flex-1 py-2 rounded-xl bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 font-bold text-xs text-center transition-all border border-blue-500/20"
                    >
                      Open & Edit
                    </button>
                  </div>
                </div>
              ))}

              {myFilings.length === 0 && (
                <div className="col-span-full text-center py-12 bg-slate-900/30 rounded-3xl border border-slate-800">
                  <FileText className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <h3 className="text-base font-bold text-white">No Filings Submitted Yet</h3>
                  <p className="text-xs text-slate-400 mt-1">Select a service to start your first filing with Luna.</p>
                  <button
                    onClick={() => setActiveTab('dashboard')}
                    className="mt-4 px-5 py-2.5 rounded-2xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs inline-flex items-center gap-2"
                  >
                    <Plus size={16} /> Start a Filing
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════════
            VIEW 3: ACTIVE FORM FILING WORKSPACE (FULL-WIDTH SLEEK SCHEDULES)
        ════════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'form_filing' && (
          <div className="space-y-6 animate-fade-in max-w-5xl mx-auto">
            {/* Top Bar with Service Info & Action Buttons */}
            <div className="bg-slate-900/80 border border-slate-800 p-5 rounded-3xl flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setActiveTab('dashboard')}
                  className="w-9 h-9 rounded-xl bg-slate-800 hover:bg-slate-700 flex items-center justify-center text-slate-300 transition-all"
                >
                  <ChevronRight size={16} className="rotate-180" />
                </button>
                <div>
                  <h2 className="text-lg font-black text-white">
                    {CORPORATE_SERVICES.find(s => s.id === activeServiceId)?.label || 'Corporate Filing'}
                  </h2>
                  <p className="text-xs text-slate-400 font-mono">Year of Assessment: 2024/2025 | Sri Lanka Inland Revenue & Statutory Compliance</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setIsLunaOpen(true);
                    setLunaViewMode('docked');
                  }}
                  className="px-4 py-2.5 rounded-2xl bg-cyan-500/20 text-cyan-300 border border-cyan-400/40 hover:bg-cyan-500/30 font-bold text-xs flex items-center gap-2 transition-all"
                >
                  <Bot size={15} /> Talk to Tenet Luna
                </button>
                <button
                  onClick={() => handleSaveFiling(false)}
                  disabled={savingFiling}
                  className="px-4 py-2.5 rounded-2xl bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold text-xs flex items-center gap-2 transition-all"
                >
                  <Save size={14} /> Save Draft
                </button>
                <button
                  onClick={() => handleSaveFiling(true)}
                  disabled={savingFiling}
                  className="px-5 py-2.5 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-black text-xs flex items-center gap-2 shadow-lg shadow-emerald-600/30 transition-all"
                >
                  <Send size={14} /> Submit to Firm for Review
                </button>
              </div>
            </div>

            {/* Schedule Sub-Tabs for Tax Filing */}
            {activeServiceId === 'tax_filling' && (
              <div className="flex items-center gap-1.5 overflow-x-auto pb-2 border-b border-slate-800 text-xs">
                <button
                  onClick={() => setActiveSchedule('header')}
                  className={`px-4 py-2 rounded-2xl font-bold whitespace-nowrap transition-all ${
                    activeSchedule === 'header' ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30' : 'bg-slate-900 text-slate-400 hover:text-white'
                  }`}
                >
                  1. Taxpayer Info
                </button>
                <button
                  onClick={() => setActiveSchedule('sched7')}
                  className={`px-4 py-2 rounded-2xl font-bold whitespace-nowrap transition-all ${
                    activeSchedule === 'sched7' ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30' : 'bg-slate-900 text-slate-400 hover:text-white'
                  }`}
                >
                  2. Business Income & Expenses
                </button>
                <button
                  onClick={() => setActiveSchedule('sched1')}
                  className={`px-4 py-2 rounded-2xl font-bold whitespace-nowrap transition-all ${
                    activeSchedule === 'sched1' ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30' : 'bg-slate-900 text-slate-400 hover:text-white'
                  }`}
                >
                  3. Other Income & APIT
                </button>
                <button
                  onClick={() => setActiveSchedule('sched3')}
                  className={`px-4 py-2 rounded-2xl font-bold whitespace-nowrap transition-all ${
                    activeSchedule === 'sched3' ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30' : 'bg-slate-900 text-slate-400 hover:text-white'
                  }`}
                >
                  4. Assets & Wealth
                </button>
                <button
                  onClick={() => setActiveSchedule('sched4')}
                  className={`px-4 py-2 rounded-2xl font-bold whitespace-nowrap transition-all ${
                    activeSchedule === 'sched4' ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30' : 'bg-slate-900 text-slate-400 hover:text-white'
                  }`}
                >
                  5. Liabilities & Loans
                </button>
              </div>
            )}

            {/* Form Fields Main Container */}
            <div className="bg-slate-900/60 backdrop-blur-md border border-slate-800 rounded-3xl p-6 sm:p-8 space-y-6 shadow-xl">

              {/* 1. Taxpayer Header Info */}
              {(activeSchedule === 'header' || activeServiceId !== 'tax_filling') && (
                <div className="space-y-4">
                  <h3 className="text-sm font-bold text-white uppercase tracking-wider text-blue-400 flex items-center gap-2">
                    <UserCheck size={16} /> Primary Entity & Taxpayer Information
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-400 mb-1">Taxpayer Full Legal Name</label>
                      <input
                        type="text"
                        value={formData.fullName}
                        onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                        className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-400 mb-1">TIN (Taxpayer Identification Number)</label>
                      <input
                        type="text"
                        value={formData.tinNumber}
                        onChange={(e) => setFormData({ ...formData, tinNumber: e.target.value })}
                        placeholder="e.g. 109283746-0000"
                        className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-cyan-300 font-mono font-bold outline-none focus:border-blue-500"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* 2. Schedule 7: Business Income & Allowable Expenses */}
              {activeServiceId === 'tax_filling' && activeSchedule === 'sched7' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between pb-2 border-b border-slate-800">
                    <h3 className="text-sm font-bold text-white uppercase tracking-wider text-emerald-400 flex items-center gap-2">
                      <DollarSign size={16} /> Schedule 7 — Computation of Business Income (Y/A 2024/2025)
                    </h3>
                    <span className="text-xs font-mono text-slate-400">Official Currency: Sri Lankan Rupee (LKR)</span>
                  </div>

                  <div className="bg-slate-950 p-6 rounded-3xl border border-slate-800 space-y-6">
                    <div>
                      <label className="block text-xs font-bold text-slate-300 mb-1.5">
                        Gross Business Turnover / Revenue for Y/A 2024/2025 (Rs.)
                      </label>
                      <input
                        type="number"
                        value={formData.grossBusinessRevenue || ''}
                        onChange={(e) => {
                          const val = Number(e.target.value);
                          setFormData((prev: any) => ({
                            ...prev,
                            grossBusinessRevenue: val,
                            netBusinessIncome: Math.max(0, val - (prev.totalExpenses || 0))
                          }));
                        }}
                        placeholder="e.g. 15000000"
                        className="w-full bg-slate-900 border border-slate-700 rounded-2xl px-5 py-3.5 text-base text-emerald-300 font-mono font-black outline-none focus:border-emerald-500 shadow-inner"
                      />
                    </div>

                    <div className="pt-4 border-t border-slate-800 space-y-4">
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block">
                        Allowable Operational Expenses & Tax Deductions
                      </span>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-bold text-slate-400 mb-1">Staff Salaries & Wages (Rs.)</label>
                          <input
                            type="number"
                            value={formData.salariesExpenses || ''}
                            onChange={(e) => setFormData({ ...formData, salariesExpenses: Number(e.target.value) })}
                            className="w-full bg-slate-900 border border-slate-800 rounded-xl px-4 py-2.5 text-xs text-white font-mono outline-none focus:border-blue-500"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-bold text-slate-400 mb-1">Rent & Utility Bills (Rs.)</label>
                          <input
                            type="number"
                            value={formData.rentUtilitiesExpenses || ''}
                            onChange={(e) => setFormData({ ...formData, rentUtilitiesExpenses: Number(e.target.value) })}
                            className="w-full bg-slate-900 border border-slate-800 rounded-xl px-4 py-2.5 text-xs text-white font-mono outline-none focus:border-blue-500"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-bold text-slate-400 mb-1">Transport & Fuel (Rs.)</label>
                          <input
                            type="number"
                            value={formData.transportExpenses || ''}
                            onChange={(e) => setFormData({ ...formData, transportExpenses: Number(e.target.value) })}
                            className="w-full bg-slate-900 border border-slate-800 rounded-xl px-4 py-2.5 text-xs text-white font-mono outline-none focus:border-blue-500"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-bold text-slate-400 mb-1">Stationery & Office Supplies (Rs.)</label>
                          <input
                            type="number"
                            value={formData.stationeryExpenses || ''}
                            onChange={(e) => setFormData({ ...formData, stationeryExpenses: Number(e.target.value) })}
                            className="w-full bg-slate-900 border border-slate-800 rounded-xl px-4 py-2.5 text-xs text-white font-mono outline-none focus:border-blue-500"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Automatic Tax Computation Card */}
                    <div className="bg-slate-900 p-5 rounded-2xl border border-slate-700 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs">
                      <div>
                        <div className="text-slate-400 font-bold">Total Allowable Deductions:</div>
                        <div className="text-lg font-black text-rose-400 font-mono">
                          Rs. {(Number(formData.salariesExpenses || 0) + Number(formData.rentUtilitiesExpenses || 0) + Number(formData.transportExpenses || 0) + Number(formData.stationeryExpenses || 0)).toLocaleString()}
                        </div>
                      </div>
                      <div className="sm:text-right">
                        <div className="text-slate-400 font-bold">Net Taxable Business Profit:</div>
                        <div className="text-2xl font-black text-emerald-400 font-mono">
                          Rs. {Number(formData.netBusinessIncome || 0).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* 3. Non-Tax Custom Services (Secretarial, HRM, Legal) */}
              {activeServiceId !== 'tax_filling' && (
                <div className="space-y-4">
                  <h3 className="text-sm font-bold text-white uppercase tracking-wider text-purple-400">
                    {CORPORATE_SERVICES.find(s => s.id === activeServiceId)?.label} Filing Specifications
                  </h3>

                  <div>
                    <label className="block text-xs font-bold text-slate-400 mb-1">Filing Subject & Scope</label>
                    <textarea
                      rows={4}
                      value={formData.generalNotes}
                      onChange={(e) => setFormData({ ...formData, generalNotes: e.target.value })}
                      placeholder="Specify your company secretarial, HRM, accounting, or legal requirements..."
                      className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-4 text-xs text-white outline-none focus:border-blue-500"
                    />
                  </div>
                </div>
              )}

              {/* Supporting Document Uploads */}
              <div className="pt-4 border-t border-slate-800 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-300 uppercase tracking-wider">
                    Attached Supporting Documents (OCR Extracted)
                  </span>
                  <label className="px-3.5 py-1.5 rounded-xl bg-blue-600/20 text-blue-300 border border-blue-500/30 hover:bg-blue-600/30 text-xs font-bold flex items-center gap-1.5 cursor-pointer transition-all">
                    {uploadingDoc ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <UploadCloud size={14} />}
                    Upload PDF / Certificate
                    <input type="file" onChange={handleFileUpload} accept=".pdf,.png,.jpg,.jpeg" className="hidden" />
                  </label>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {uploadedDocs.map((doc, idx) => (
                    <div key={idx} className="bg-slate-950 p-3 rounded-2xl border border-slate-800 flex items-center justify-between text-xs font-mono">
                      <span className="truncate text-slate-300">{doc.originalName || doc.filename}</span>
                      <span className="text-emerald-400 text-[10px] uppercase font-bold shrink-0 ml-2">Attached</span>
                    </div>
                  ))}
                  {uploadedDocs.length === 0 && (
                    <p className="text-xs text-slate-500 italic col-span-full">No documents uploaded yet. You can attach T-10, bank statements, or certificates.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* ════════════════════════════════════════════════════════════════════════
          TENET LUNA 3D CHIBI BOT FLOATING TRIGGER (LIKE ON TENET WEBSITES)
      ════════════════════════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {!isLunaOpen && (
          <motion.div
            key="tenet-luna-chibi-floating-widget"
            initial={{ scale: 0, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0, opacity: 0, y: 50 }}
            className="fixed bottom-6 right-6 z-50 flex flex-col items-center cursor-pointer group"
            onClick={() => {
              setIsLunaOpen(true);
              setLunaViewMode('docked');
            }}
          >
            {/* Speech Tag Pill above 3D Bot */}
            <div className="mb-2 bg-slate-950/95 text-cyan-300 border border-cyan-400/60 px-3.5 py-1.5 rounded-full text-[11px] font-black tracking-wide shadow-[0_0_25px_rgba(6,182,212,0.5)] backdrop-blur-md whitespace-nowrap animate-bounce flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-cyan-400 animate-ping" />
              🎙️ Speak with Tenet Luna
            </div>

            {/* 3D Chibi Spherical Bot Container */}
            <div className="w-24 h-24 sm:w-28 sm:h-28 relative filter drop-shadow-[0_15px_30px_rgba(6,182,212,0.45)] group-hover:scale-110 group-hover:-translate-y-2 transition-all duration-300">
              <LunaFace
                faceId={firm?.activeFaceId || 'cute_chibi_bot'}
                sentiment="happy"
                isListening={false}
                isSpeaking={false}
                className="w-full h-full"
                themeColor={firm?.themeColor || '#06b6d4'}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ════════════════════════════════════════════════════════════════════════
          TENET LUNA INTERACTIVE CO-PILOT (DOCKED SIDEBAR / FULLSCREEN MODAL)
      ════════════════════════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {isLunaOpen && (
          <motion.div
            key="tenet-luna-live-container"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className={`fixed z-[9999] bg-[#05070B]/95 backdrop-blur-2xl border border-cyan-500/40 shadow-[0_25px_70px_rgba(0,0,0,0.9),0_0_50px_rgba(6,182,212,0.25)] flex flex-col overflow-hidden ${
              lunaViewMode === 'fullscreen'
                ? 'inset-0 rounded-none'
                : 'bottom-4 right-4 sm:bottom-6 sm:right-6 w-[calc(100vw-32px)] sm:w-[440px] h-[600px] max-h-[calc(100vh-48px)] rounded-3xl'
            }`}
          >
            {/* Header Control Bar */}
            <div className="p-3.5 px-4 flex items-center justify-between border-b border-cyan-500/20 bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-cyan-500/20 border border-cyan-400/50 flex items-center justify-center text-cyan-300 shadow-md shadow-cyan-500/20">
                  <Bot size={16} />
                </div>
                <div>
                  <h3 className="text-xs font-black text-white flex items-center gap-1.5">
                    Tenet Luna Voice Co-Pilot
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  </h3>
                  <p className="text-[10px] text-cyan-300 font-mono">Live Statutory Tax & Filing AI</p>
                </div>
              </div>

              {/* Window Controls */}
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setLunaViewMode(lunaViewMode === 'fullscreen' ? 'docked' : 'fullscreen')}
                  className="w-7 h-7 rounded-lg bg-white/10 hover:bg-white/20 border border-white/10 text-white flex items-center justify-center transition-all text-xs"
                  title={lunaViewMode === 'fullscreen' ? 'Dock to Corner' : 'Maximize Fullscreen'}
                >
                  {lunaViewMode === 'fullscreen' ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
                </button>
                <button
                  onClick={() => setIsLunaOpen(false)}
                  className="w-7 h-7 rounded-lg bg-rose-500/20 hover:bg-rose-500/40 border border-rose-500/30 text-rose-300 flex items-center justify-center transition-all text-xs"
                  title="Minimize Luna"
                >
                  <X size={13} />
                </button>
              </div>
            </div>

            {/* Luna Live Voice & Multimodal Component */}
            <div className="flex-1 relative w-full h-full overflow-hidden bg-gradient-to-b from-transparent to-black/80">
              <LunaVoiceModel
                apiKey={firm?.apiKey || ''}
                role="corporate"
                storeName={firm?.name || 'Corporate Firm'}
                onDisconnect={() => setIsLunaOpen(false)}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
